import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { 
  ArrowLeft, 
  ArrowRight, 
  Heart, 
  Shield, 
  AlertTriangle, 
  MessageCircle, 
  UserCheck,
  CheckCircle,
  BookOpen,
  XCircle,
  ClipboardCheck,
  RotateCcw
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Quiz questions based on training content
const quizQuestions = [
  {
    id: 1,
    question: "As a VALORLink volunteer, your role is to provide:",
    options: [
      { value: "a", label: "Medical advice and treatment recommendations" },
      { value: "b", label: "Presence, empathy, understanding, and peer connection" },
      { value: "c", label: "Diagnostic assessments and clinical guidance" },
      { value: "d", label: "Emergency response services" }
    ],
    correct: "b"
  },
  {
    id: 2,
    question: "Which of the following is NOT part of your role as a volunteer?",
    options: [
      { value: "a", label: "Providing peer support" },
      { value: "b", label: "Recommending or adjusting medications" },
      { value: "c", label: "Offering empathy and understanding" },
      { value: "d", label: "Being present for fellow veterans" }
    ],
    correct: "b"
  },
  {
    id: 3,
    question: "When should you escalate a situation?",
    options: [
      { value: "a", label: "Only when the user specifically asks you to" },
      { value: "b", label: "Never - you should always handle it yourself" },
      { value: "c", label: "When a user expresses thoughts of self-harm, threatens harm to others, or makes you uncomfortable about safety" },
      { value: "d", label: "Only after getting permission from an administrator" }
    ],
    correct: "c"
  },
  {
    id: 4,
    question: "Escalation is:",
    options: [
      { value: "a", label: "A sign of failure as a volunteer" },
      { value: "b", label: "Responsible care and an important part of your role" },
      { value: "c", label: "Something to avoid at all costs" },
      { value: "d", label: "Only for emergencies involving physical danger" }
    ],
    correct: "b"
  },
  {
    id: 5,
    question: "Which phrase is APPROPRIATE to say to someone you are supporting?",
    options: [
      { value: "a", label: "\"I know exactly how you feel.\"" },
      { value: "b", label: "\"Everything will be okay.\"" },
      { value: "c", label: "\"You don't have to handle this alone.\"" },
      { value: "d", label: "\"I can help you through this myself.\"" }
    ],
    correct: "c"
  },
  {
    id: 6,
    question: "What should you do if you feel emotionally overwhelmed while supporting someone?",
    options: [
      { value: "a", label: "Push through and continue helping" },
      { value: "b", label: "Take breaks when needed and reach out to VALOR leadership for support" },
      { value: "c", label: "Stop volunteering permanently" },
      { value: "d", label: "Tell the user you can't help them" }
    ],
    correct: "b"
  },
  {
    id: 7,
    question: "You should escalate if a user:",
    options: [
      { value: "a", label: "Asks about your military background" },
      { value: "b", label: "Wants to schedule a follow-up conversation" },
      { value: "c", label: "Becomes unresponsive during an active session" },
      { value: "d", label: "Shares a positive update about their day" }
    ],
    correct: "c"
  },
  {
    id: 8,
    question: "As a VALORLink volunteer, you are expected to:",
    options: [
      { value: "a", label: "Promise positive outcomes to those you support" },
      { value: "b", label: "Replace emergency services when needed" },
      { value: "c", label: "Stay within defined role boundaries to protect yourself and those you support" },
      { value: "d", label: "Provide clinical advice based on your personal experience" }
    ],
    correct: "c"
  },
  {
    id: 9,
    question: "Which phrase should you AVOID saying?",
    options: [
      { value: "a", label: "\"I'm really glad you reached out.\"" },
      { value: "b", label: "\"There are people available right now who can help keep you safe.\"" },
      { value: "c", label: "\"You don't need outside help.\"" },
      { value: "d", label: "\"You don't have to handle this alone.\"" }
    ],
    correct: "c"
  },
  {
    id: 10,
    question: "Your value as a VALORLink volunteer comes from:",
    options: [
      { value: "a", label: "Your ability to diagnose and treat conditions" },
      { value: "b", label: "Shared experience and human connection" },
      { value: "c", label: "Providing professional medical advice" },
      { value: "d", label: "Replacing crisis hotlines and emergency services" }
    ],
    correct: "b"
  }
];

const PASSING_SCORE = 80; // 80% required to pass

const VolunteerTraining = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [acknowledged, setAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  
  // Quiz state
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [quizPassed, setQuizPassed] = useState(false);

  // Check if user already completed training
  useEffect(() => {
    if (user?.training_completed) {
      setCompleted(true);
    }
  }, [user]);

  const sections = [
    {
      id: "welcome",
      title: "Welcome",
      icon: Heart,
      content: (
        <div className="space-y-4">
          <p className="text-lg text-foreground">
            Thank you for volunteering with VALORLink.
          </p>
          <p className="text-muted-foreground">
            Your role is essential, meaningful, and clearly defined.
          </p>
          <p className="text-muted-foreground">
            This training explains what is expected of you, what is not expected of you, and how to protect both users and yourself while providing peer support.
          </p>
        </div>
      )
    },
    {
      id: "your-role",
      title: "Your Role as a Volunteer",
      icon: UserCheck,
      content: (
        <div className="space-y-4">
          <p className="text-muted-foreground">
            As a VALORLink volunteer, your role is to provide:
          </p>
          <ul className="space-y-2 ml-4">
            <li className="flex items-center gap-3">
              <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
              <span className="text-foreground font-medium">Presence</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
              <span className="text-foreground font-medium">Empathy</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
              <span className="text-foreground font-medium">Understanding</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
              <span className="text-foreground font-medium">Peer connection</span>
            </li>
          </ul>
          <div className="bg-muted/50 rounded-lg p-4 mt-6">
            <p className="text-muted-foreground">
              You are <strong className="text-foreground">not</strong> expected to fix problems, give advice, or provide professional services.
            </p>
            <p className="text-muted-foreground mt-2">
              Your value comes from shared experience and human connection.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "what-you-dont-do",
      title: "What You Do NOT Do",
      icon: Shield,
      content: (
        <div className="space-y-4">
          <p className="text-muted-foreground">You do not:</p>
          <ul className="space-y-3 ml-4">
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
              <span className="text-foreground">Diagnose conditions</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
              <span className="text-foreground">Provide medical, mental health, or clinical advice</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
              <span className="text-foreground">Recommend or adjust medications</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
              <span className="text-foreground">Promise outcomes</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
              <span className="text-foreground">Replace emergency services or crisis hotlines</span>
            </li>
          </ul>
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mt-6">
            <p className="text-foreground font-medium">
              Staying within these boundaries protects you and the people you support.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "safety-escalation",
      title: "Safety and Escalation",
      icon: AlertTriangle,
      content: (
        <div className="space-y-4">
          <p className="text-muted-foreground">
            Sometimes a conversation may indicate immediate danger or serious distress.
          </p>
          <p className="text-foreground font-medium mt-4">
            You must escalate when a user:
          </p>
          <ul className="space-y-3 ml-4 mt-2">
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
              <span className="text-foreground">Expresses thoughts of self-harm or suicide</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
              <span className="text-foreground">Threatens harm to others</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
              <span className="text-foreground">Requests medical or clinical guidance</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
              <span className="text-foreground">Becomes unresponsive during an active session</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
              <span className="text-foreground">Makes you uncomfortable or unsure about safety</span>
            </li>
          </ul>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
            <p className="text-green-800 font-semibold">Escalation is not failure.</p>
            <p className="text-green-700">Escalation is responsible care.</p>
          </div>
        </div>
      )
    },
    {
      id: "escalation-process",
      title: "What Escalation Looks Like",
      icon: Shield,
      content: (
        <div className="space-y-4">
          <p className="text-muted-foreground">When escalation is needed:</p>
          <ul className="space-y-3 ml-4 mt-2">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-foreground">Use the escalation tools provided in the platform</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-foreground">Encourage connection to emergency or crisis resources</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-foreground">Maintain a calm and supportive tone</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-foreground">Do not attempt to manage the situation alone</span>
            </li>
          </ul>
        </div>
      )
    },
    {
      id: "what-to-say",
      title: "What to Say (Examples)",
      icon: MessageCircle,
      content: (
        <div className="space-y-6">
          <div>
            <h4 className="text-green-700 font-semibold mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Appropriate
            </h4>
            <ul className="space-y-2 ml-4">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-foreground italic">&ldquo;I&apos;m really glad you reached out.&rdquo;</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-foreground italic">&ldquo;You don&apos;t have to handle this alone.&rdquo;</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-foreground italic">&ldquo;There are people available right now who can help keep you safe.&rdquo;</span>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-red-700 font-semibold mb-3 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center">✕</span>
              Avoid
            </h4>
            <ul className="space-y-2 ml-4">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-foreground italic">&ldquo;I know exactly how you feel.&rdquo;</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-foreground italic">&ldquo;Everything will be okay.&rdquo;</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-foreground italic">&ldquo;You don&apos;t need outside help.&rdquo;</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-foreground italic">&ldquo;I can help you through this myself.&rdquo;</span>
              </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "protecting-yourself",
      title: "Protecting Yourself",
      icon: Heart,
      content: (
        <div className="space-y-4">
          <p className="text-muted-foreground">
            Supporting others can be emotionally demanding.
          </p>
          <p className="text-foreground font-medium mt-4">You are encouraged to:</p>
          <ul className="space-y-3 ml-4 mt-2">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-foreground">Take breaks when needed</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-foreground">Escalate early if unsure</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-foreground">Reach out to VALOR leadership if support is needed</span>
            </li>
          </ul>
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mt-6">
            <p className="text-foreground font-medium">
              You are never expected to handle crisis situations alone.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "quiz",
      title: "Knowledge Check",
      icon: ClipboardCheck,
      content: null // Quiz content is rendered separately
    },
    {
      id: "acknowledgment",
      title: "Acknowledgment",
      icon: BookOpen,
      content: (
        <div className="space-y-4">
          <p className="text-muted-foreground">By continuing, you acknowledge that:</p>
          <ul className="space-y-3 ml-4 mt-2">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-foreground">You understand your role as a peer-support volunteer</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-foreground">You agree to follow escalation and safety guidelines</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-foreground">You will not provide clinical or emergency services through VALORLink</span>
            </li>
          </ul>
        </div>
      )
    }
  ];

  const handleQuizAnswer = (questionId, value) => {
    setQuizAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmitQuiz = () => {
    // Check if all questions are answered
    const answeredCount = Object.keys(quizAnswers).length;
    if (answeredCount < quizQuestions.length) {
      toast.error(`Please answer all questions (${answeredCount}/${quizQuestions.length} answered)`);
      return;
    }

    // Calculate score
    let correctCount = 0;
    quizQuestions.forEach(q => {
      if (quizAnswers[q.id] === q.correct) {
        correctCount++;
      }
    });

    const score = Math.round((correctCount / quizQuestions.length) * 100);
    setQuizScore(score);
    setQuizSubmitted(true);

    if (score >= PASSING_SCORE) {
      setQuizPassed(true);
      toast.success(`Congratulations! You passed with ${score}%`);
    } else {
      toast.error(`You scored ${score}%. You need ${PASSING_SCORE}% to pass.`);
    }
  };

  const handleRetryQuiz = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(0);
    setQuizPassed(false);
  };

  const handleComplete = async () => {
    if (!acknowledged) {
      toast.error("Please acknowledge the volunteer guidelines to continue");
      return;
    }

    if (!quizPassed) {
      toast.error("Please pass the knowledge check before completing training");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/training/complete`, {
        quiz_score: quizScore
      });
      if (setUser && response.data) {
        setUser(prev => ({ ...prev, training_completed: true, training_completed_at: new Date().toISOString() }));
      }
      setCompleted(true);
      toast.success("Training completed successfully!");
    } catch (error) {
      console.error("Error completing training:", error);
      toast.error("Failed to record training completion. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextSection = () => {
    // If on quiz section and quiz not passed, don't allow proceeding
    if (sections[currentSection].id === "quiz" && !quizPassed) {
      toast.error("Please pass the knowledge check to continue");
      return;
    }
    
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      window.scrollTo(0, 0);
    }
  };

  // Completion screen
  if (completed) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-white border-b border-border sticky top-0 z-40">
          <div className="va-blue-bar h-1"></div>
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="hover:bg-muted"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-8 w-auto" />
                <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  VALORLink<span className="text-xs text-primary font-bold align-super">™</span>
                </span>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-16 max-w-lg">
          <Card className="text-center" data-testid="training-complete-card">
            <CardContent className="pt-8 pb-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                Training Complete
              </h2>
              <p className="text-muted-foreground mb-6">
                Thank you for completing the VALORLink Volunteer Role & Safety Training. You are now ready to support fellow veterans.
              </p>
              <Button onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn">
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const currentSectionData = sections[currentSection];
  const isLastSection = currentSection === sections.length - 1;
  const isQuizSection = currentSectionData.id === "quiz";
  const SectionIcon = currentSectionData.icon;

  // Render quiz content
  const renderQuizContent = () => {
    if (quizSubmitted) {
      return (
        <div className="space-y-6">
          {/* Quiz Results */}
          <div className={`p-6 rounded-lg text-center ${quizPassed ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${quizPassed ? 'bg-green-100' : 'bg-red-100'}`}>
              {quizPassed ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : (
                <XCircle className="w-8 h-8 text-red-600" />
              )}
            </div>
            <h3 className={`text-xl font-bold mb-2 ${quizPassed ? 'text-green-800' : 'text-red-800'}`}>
              {quizPassed ? 'You Passed!' : 'Not Quite'}
            </h3>
            <p className={`text-lg font-semibold mb-2 ${quizPassed ? 'text-green-700' : 'text-red-700'}`}>
              Your Score: {quizScore}%
            </p>
            <p className="text-muted-foreground">
              {quizPassed 
                ? 'Great job! You can now proceed to complete your training.' 
                : `You need ${PASSING_SCORE}% to pass. Please review the material and try again.`}
            </p>
            {!quizPassed && (
              <Button 
                onClick={handleRetryQuiz} 
                className="mt-4 gap-2"
                variant="outline"
              >
                <RotateCcw className="w-4 h-4" />
                Retry Quiz
              </Button>
            )}
          </div>

          {/* Show answers review */}
          <div className="space-y-4">
            <h4 className="font-semibold text-foreground">Review Your Answers:</h4>
            {quizQuestions.map((q, index) => {
              const userAnswer = quizAnswers[q.id];
              const isCorrect = userAnswer === q.correct;
              return (
                <div 
                  key={q.id} 
                  className={`p-4 rounded-lg border ${isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                >
                  <p className="font-medium text-foreground mb-2">
                    {index + 1}. {q.question}
                  </p>
                  <p className={`text-sm ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                    Your answer: {q.options.find(o => o.value === userAnswer)?.label}
                  </p>
                  {!isCorrect && (
                    <p className="text-sm text-green-700 mt-1">
                      Correct answer: {q.options.find(o => o.value === q.correct)?.label}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
          <p className="text-foreground font-medium">
            You must score at least {PASSING_SCORE}% to pass this knowledge check.
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Answer all {quizQuestions.length} questions based on what you learned in the training.
          </p>
        </div>

        {quizQuestions.map((q, index) => (
          <div key={q.id} className="space-y-3 p-4 bg-muted/30 rounded-lg">
            <p className="font-medium text-foreground">
              {index + 1}. {q.question}
            </p>
            <RadioGroup
              value={quizAnswers[q.id] || ""}
              onValueChange={(value) => handleQuizAnswer(q.id, value)}
              className="space-y-2"
            >
              {q.options.map((option) => (
                <div key={option.value} className="flex items-start gap-3 p-2 rounded hover:bg-muted/50">
                  <RadioGroupItem 
                    value={option.value} 
                    id={`q${q.id}-${option.value}`}
                    className="mt-0.5"
                  />
                  <Label 
                    htmlFor={`q${q.id}-${option.value}`}
                    className="text-foreground cursor-pointer flex-1"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        ))}

        <Button 
          onClick={handleSubmitQuiz}
          className="w-full"
          data-testid="submit-quiz-btn"
        >
          Submit Quiz
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="va-blue-bar h-1"></div>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="hover:bg-muted"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-8 w-auto" />
                <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  VALORLink<span className="text-xs text-primary font-bold align-super">™</span>
                </span>
              </div>
            </div>
            <span className="text-sm text-muted-foreground">
              {currentSection + 1} / {sections.length}
            </span>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-muted/30">
        <div 
          className="h-1 bg-primary transition-all duration-300"
          style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
        ></div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Module Title (shown only on first section) */}
        {currentSection === 0 && (
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
              VALORLink Volunteer Role & Safety Training
            </h1>
            <p className="text-muted-foreground">
              Required training for all volunteers
            </p>
          </div>
        )}

        {/* Section Card */}
        <Card className="mb-8" data-testid={`training-section-${currentSectionData.id}`}>
          <CardContent className="pt-6">
            {/* Section Header */}
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <SectionIcon className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                {currentSectionData.title}
              </h2>
            </div>

            {/* Section Content */}
            <div className="min-h-[200px]">
              {isQuizSection ? renderQuizContent() : currentSectionData.content}
            </div>

            {/* Acknowledgment Checkbox (only on last section) */}
            {isLastSection && (
              <div className="mt-8 pt-6 border-t border-border">
                {!quizPassed && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <p className="text-amber-800 text-sm">
                      You must pass the Knowledge Check before completing the training.
                    </p>
                  </div>
                )}
                <label className={`flex items-start gap-3 cursor-pointer p-4 rounded-lg border transition-colors ${
                  quizPassed 
                    ? 'border-border hover:border-primary' 
                    : 'border-muted bg-muted/30 cursor-not-allowed'
                }`}>
                  <Checkbox 
                    checked={acknowledged}
                    onCheckedChange={quizPassed ? setAcknowledged : undefined}
                    disabled={!quizPassed}
                    className="mt-0.5"
                    data-testid="training-acknowledge-checkbox"
                  />
                  <span className={`font-medium ${quizPassed ? 'text-foreground' : 'text-muted-foreground'}`}>
                    I understand and agree to these volunteer guidelines.
                  </span>
                </label>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={prevSection}
            disabled={currentSection === 0}
            className="gap-2"
            data-testid="training-prev-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </Button>

          {isLastSection ? (
            <Button
              onClick={handleComplete}
              disabled={!acknowledged || !quizPassed || isSubmitting}
              className="gap-2"
              data-testid="training-complete-btn"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Completing...
                </>
              ) : (
                <>
                  Complete Training
                  <CheckCircle className="w-4 h-4" />
                </>
              )}
            </Button>
          ) : isQuizSection ? (
            <Button
              onClick={nextSection}
              disabled={!quizPassed}
              className="gap-2"
              data-testid="training-next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={nextSection}
              className="gap-2"
              data-testid="training-next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Section Dots */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {sections.map((section, index) => (
            <button
              key={section.id}
              onClick={() => {
                // Don't allow skipping past quiz if not passed
                if (index > sections.findIndex(s => s.id === "quiz") && !quizPassed) {
                  toast.error("Please pass the knowledge check first");
                  return;
                }
                setCurrentSection(index);
              }}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentSection 
                  ? 'bg-primary' 
                  : index < currentSection 
                    ? 'bg-primary/50' 
                    : 'bg-muted-foreground/30'
              }`}
              aria-label={`Go to section ${index + 1}`}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default VolunteerTraining;
